import { CommonModule, NgIf, TitleCasePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCommentAvatarDirective, NzCommentModule } from 'ng-zorro-antd/comment';
import { NZ_DRAWER_DATA } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AuthService, ChatService, TimezoneAwareDatePipe } from '@twaice-fe/frontend/shared/services';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { A11yModule } from '@angular/cdk/a11y';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ChatMessage } from '@twaice-fe/shared/models';
import { setChatLauncherHidden } from '@twaice-fe/frontend/shared/utilities';
import { Observable, startWith } from 'rxjs';
import mixpanel from 'mixpanel-browser';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { SvgIconModule } from '../../svg-icon/svg-icon.module';
import { ChatMentionsPipe } from '../chat-mentions/chat-mentions.pipe';
import { NzDemoTagControlComponent } from './message-tag.component';

@Component({
  selector: 'twaice-fe-system-chat',
  imports: [
    FormsModule,
    NzCommentModule,
    NzCommentAvatarDirective,
    NzAvatarModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSpinModule,
    NgIf,
    SvgIconModule,
    NzIconModule,
    NzDividerModule,
    NzDemoTagControlComponent,
    NzSelectModule,
    SvgIconModule,
    NzEmptyModule,
    NzSwitchModule,
    A11yModule,
    ChatMentionsPipe,
    TitleCasePipe,
    CommonModule,
    NzToolTipModule,
    TimezoneAwareDatePipe,
  ],
  styleUrl: './chat.component.scss',
  templateUrl: './chat.component.html',
  standalone: true,
})
export class SystemChatComponent implements AfterViewInit, OnChanges, OnInit, OnDestroy {
  @ViewChildren('commentContent') comments: QueryList<ElementRef>;

  /** Falls back to `drawerData` when unset, for drawer-hosted callers. */
  @Input() customerBk?: string;
  @Input() systemBk?: string;
  @Input() defaultTags?: string[];
  @Input() tagFilter?: string[];
  @Input() scrollToNoteId?: string;
  /** Tags every in-scope message must carry, but hidden from pills and the tag filter/editor. */
  @Input() hiddenTags?: string[];
  /** Tags that name a message's group (e.g. onboarding's section tags) — shown as a heading above
   * each contiguous run, hidden from pills like `hiddenTags` but still usable as a filter scope. */
  @Input() groupTags?: string[];
  /** Overrides a `groupTags` entry's heading text when the tag itself isn't human-readable (e.g.
   * a document's `document:<id>` tag, labeled with its filename). A `groupTags` entry with no
   * entry here renders as plain text using the tag itself, same as before this existed; one with
   * an entry renders as a clickable heading emitting `headingClick`. */
  @Input() groupLabels?: Record<string, string>;
  /** Whether a comment can carry a "go to Dashboard" link back to where it was created. */
  @Input() allowSourceLink?: boolean;
  /** An existing message stream to reuse instead of fetching independently — avoids a duplicate
   * `fetchChatMessages` call when the caller already has one (e.g. `OnboardingCommentsService`). */
  @Input() messages$?: Observable<ChatMessage[]>;
  /** What a single entry is called in user-facing copy (empty state, composer placeholder, delete
   * confirmation) — onboarding calls them "comment"s. Unset keeps the existing generic copy. */
  @Input() messageNoun?: string;
  /** Emits a `groupTags` entry's tag when its (labeled, so clickable) heading is clicked — e.g.
   * for the caller to resolve a document tag back to a file and trigger its download. */
  @Output() headingClick = new EventEmitter<string>();

  isLoading = true;
  chatMessages: ChatMessage[] = [];
  filteredChatMessages: ChatMessage[] = [];
  tags: string[] = [];
  selectedTags: string[] = [];
  includeLink = true;
  newMessage = '';
  newTags: string[] = [];
  parentMessage: ChatMessage | null = null;
  submittingMessage = false;
  // Read from the template, so unlike the other resolved fields below they aren't private.
  resolvedAllowSourceLink = true;
  emptyText = 'No notes yet';
  composerPlaceholder = 'Add a new note here...';

  private readonly drawerData = inject(NZ_DRAWER_DATA, { optional: true });
  private hasScrolledToHighlight = false;
  // Resolved once in resolveInputs() rather than read per change-detection pass.
  private resolvedCustomerBk: string;
  private resolvedSystemBk: string;
  private resolvedDefaultTags: string[];
  private resolvedTagFilter: string[] | undefined;
  private resolvedScrollToNoteId: string | undefined;
  private resolvedHiddenTags: string[];
  private resolvedGroupTags: string[];
  private resolvedGroupLabels: Record<string, string>;
  private resolvedMessages$: Observable<ChatMessage[]> | undefined;
  private highlightTime: number | null = null;
  // Stays false for drawer-hosted usage — no @Input()s bound there, so ngOnChanges never fires.
  private hasReceivedFirstChange = false;

  constructor(
    protected authService: AuthService,
    private chatService: ChatService,
    private modalService: NzModalService,
    private destroy$: DestroyRef,
    private cd: ChangeDetectorRef,
    private router: Router,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit() {
    this.resolveInputs();

    // hide the chat launcher as it would overlap bottom-right actions in the drawer
    setChatLauncherHidden(true);
  }

  /** Only fires for an inline embed — reacts to the caller changing tagFilter/defaultTags/system
   * after the initial load (handled by ngOnInit/ngAfterViewInit). */
  ngOnChanges(changes: SimpleChanges): void {
    const isFirstChange = !this.hasReceivedFirstChange;
    this.hasReceivedFirstChange = true;
    this.resolveInputs();
    if (isFirstChange) return;

    if (changes['customerBk'] || changes['systemBk']) {
      this.fetchMessages();
      return;
    }

    if (changes['scrollToNoteId']) {
      // Reset the once-per-load latch so a later highlighted comment also gets scrolled to.
      this.hasScrolledToHighlight = false;
    }

    if (changes['tagFilter'] || changes['defaultTags']) {
      this.recomputeAvailableTags();
      this.filterByTag(this.visibleTags(this.resolvedTagFilter ?? []));
      if (!this.newMessage.trim()) {
        this.newTags = this.getDefaultNewTags();
      }
      this.cd.detectChanges();
    }
  }

  ngOnDestroy() {
    setChatLauncherHidden(false);
  }

  ngAfterViewInit(): void {
    if (this.resolvedMessages$) {
      // The caller's stream already reacts to notesChanged$ itself — just consume it.
      this.resolvedMessages$.pipe(takeUntilDestroyed(this.destroy$)).subscribe((messages) => this.applyMessages(messages));
    } else {
      // Refetches on any note change, not just this component's own actions.
      this.chatService.notesChanged$
        .pipe(startWith(undefined), takeUntilDestroyed(this.destroy$))
        .subscribe(() => this.fetchMessages());
    }

    this.newTags = this.getDefaultNewTags();

    this.comments.changes.pipe(takeUntilDestroyed(this.destroy$)).subscribe(() => {
      if (!this.comments || !this.comments.last) return;

      if (this.scrollToHighlightedNote()) return;

      this.comments.last.nativeElement.scrollIntoView();
    });
  }

  isHighlighted(message: ChatMessage): boolean {
    return this.highlightTime !== null && new Date(message.timestamp).getTime() === this.highlightTime;
  }

  fetchMessages(): void {
    // No-op when an external messages$ was provided — ngAfterViewInit already subscribes to it.
    if (this.resolvedMessages$) return;

    this.chatService
      .fetchChatMessages(this.resolvedCustomerBk, this.resolvedSystemBk)
      .pipe(takeUntilDestroyed(this.destroy$))
      .subscribe((messages) => this.applyMessages(messages));
  }

  postMessage() {
    this.submittingMessage = true;
    this.chatService
      .postChatMessage(
        this.resolvedCustomerBk,
        this.resolvedSystemBk,
        this.newMessage,
        this.newTags,
        this.includeLink && this.resolvedAllowSourceLink,
        this.parentMessage?.timestamp
      )
      .subscribe(() => {
        this.newMessage = '';
        this.submittingMessage = false;
        // A sent reply leaves reply mode (and its inherited tags), so the next one is a new thread.
        if (this.parentMessage) this.replyToMessage(null);
      });
  }

  openDeleteModal(message: ChatMessage) {
    const noun = this.messageNoun ?? 'message';
    const capitalizedNoun = noun.charAt(0).toUpperCase() + noun.slice(1);
    this.modalService.confirm({
      nzIconType: 'warning',
      nzWidth: 600,
      nzTitle: `Delete ${capitalizedNoun}`,
      nzContent: `Do you want to delete the ${noun}?`,
      nzOkText: `Delete ${capitalizedNoun}`,
      nzOkDanger: true,
      nzOnOk: () => {
        this.chatService.deleteChatMessage(this.resolvedCustomerBk, this.resolvedSystemBk, message.timestamp).subscribe();
      },
    });
  }

  /** `hiddenTags` are enforced as a mandatory AND requirement independent of `selectedTags`, so
   * they can't be filtered away by clearing a chip that was never shown. */
  filterByTag(tags: string[] | undefined): void {
    this.selectedTags = this.visibleTags(tags ?? []);
    // Filter out nested comments (children) from the main list - they will be displayed as nested
    const topLevelMessages = this.chatMessages.filter((m) => !m.parent);
    const hidden = this.resolvedHiddenTags;

    const matching = topLevelMessages.filter((m) => {
      if (!hidden.every((t) => m.tags.includes(t))) return false;
      return this.selectedTags.length === 0 || m.tags.some((t) => this.selectedTags.includes(t));
    });

    this.filteredChatMessages = this.groupByGroupTags(matching);
  }

  goToSourceUrl(url: string): void {
    let protocol = 'https';
    if (location.host.includes('localhost')) {
      protocol = 'http';
    }

    mixpanel.track('Note Link Followed', { sourceUrl: `${protocol}://${location.host}${url}` });

    window.open(`${protocol}://${location.host}${url}`, '_blank');
  }

  updateTags(tags: string[], message: ChatMessage): void {
    this.chatService
      .patchChatMessage(this.resolvedCustomerBk, this.resolvedSystemBk, message.timestamp, undefined, tags)
      .subscribe();
  }

  hasChildren(message: ChatMessage): boolean {
    return this.chatMessages.some((m) => m.parent === message.timestamp);
  }

  getChildren(message: ChatMessage): ChatMessage[] {
    return this.chatMessages.filter((m) => m.parent === message.timestamp);
  }

  /** Enters reply mode for `message`, pre-filling the composer with every tag it carries so the
   * reply stays in the same scope — `null` leaves reply mode and restores the default tags. */
  replyToMessage(message: ChatMessage | null): void {
    this.parentMessage = message;
    this.newTags = message ? Array.from(new Set([...this.getDefaultNewTags(), ...message.tags])) : this.getDefaultNewTags();
  }

  /** The heading to show above `message` — `undefined` unless it starts a new group's run.
   * `clickable` is true when `groupLabels` has an entry for the tag (e.g. a document), so the
   * template can render it as a link and the caller can resolve `headingClick`'s tag. */
  sectionHeadingFor(message: ChatMessage, index: number): { tag: string; label: string; clickable: boolean } | undefined {
    const tag = this.groupTagFor(message);
    if (!tag) return undefined;

    const previous = this.filteredChatMessages[index - 1];
    if (previous && this.groupTagFor(previous) === tag) return undefined;

    const label = this.resolvedGroupLabels[tag];
    return { tag, label: label ?? tag, clickable: label !== undefined };
  }

  emitHeadingClick(tag: string): void {
    this.headingClick.emit(tag);
  }

  /** The "Filter by tag" dropdown/chip text for `tag` — `groupLabels`' entry if it has one (e.g.
   * a document's filename instead of its opaque `document:<id>` tag), else the tag itself. */
  tagLabel(tag: string): string {
    return this.resolvedGroupLabels[tag] ?? tag;
  }

  /** Strips `hiddenTags` for the filter dropdown/selection. Keeps `groupTags` visible here since
   * they must still work as a filter scope; see `pillTags` for the variant that hides both. */
  visibleTags(tags: string[]): string[] {
    return tags.filter((tag) => !this.resolvedHiddenTags.includes(tag));
  }

  /** Strips both `hiddenTags` and `groupTags` for pill display/editing. Callers writing tags back
   * must re-add whichever were stripped, or an edit would silently drop them. */
  pillTags(tags: string[]): string[] {
    return tags.filter((tag) => !this.resolvedHiddenTags.includes(tag) && !this.resolvedGroupTags.includes(tag));
  }

  /** Re-adds the stripped hidden/group tags before saving an edit. */
  onMessageTagsChanged(editedPillTags: string[], message: ChatMessage): void {
    const stripped = message.tags.filter((tag) => this.resolvedHiddenTags.includes(tag) || this.resolvedGroupTags.includes(tag));
    this.updateTags([...stripped, ...editedPillTags], message);
  }

  /** Same idea as `onMessageTagsChanged`, for the new-message composer's tag editor. */
  onNewTagsChanged(editedPillTags: string[]): void {
    const stripped = this.newTags.filter((tag) => this.resolvedHiddenTags.includes(tag) || this.resolvedGroupTags.includes(tag));
    this.newTags = [...stripped, ...editedPillTags];
  }

  private resolveInputs(): void {
    this.resolvedCustomerBk = this.customerBk ?? this.drawerData?.customerBk;
    this.resolvedSystemBk = this.systemBk ?? this.drawerData?.systemBk;
    this.resolvedDefaultTags = this.defaultTags ?? this.drawerData?.defaultTags ?? [];
    this.resolvedTagFilter = this.tagFilter ?? this.drawerData?.tagFilter;
    this.resolvedScrollToNoteId = this.scrollToNoteId ?? this.drawerData?.scrollToNoteId;
    this.resolvedHiddenTags = this.hiddenTags ?? this.drawerData?.hiddenTags ?? [];
    this.resolvedGroupTags = this.groupTags ?? this.drawerData?.groupTags ?? [];
    this.resolvedGroupLabels = this.groupLabels ?? this.drawerData?.groupLabels ?? {};
    this.resolvedAllowSourceLink = this.allowSourceLink ?? this.drawerData?.allowSourceLink ?? true;
    this.resolvedMessages$ = this.messages$ ?? this.drawerData?.messages$;
    this.highlightTime = this.resolvedScrollToNoteId ? new Date(this.resolvedScrollToNoteId).getTime() : null;
    this.emptyText = `No ${this.messageNoun ?? 'note'}s yet`;
    this.composerPlaceholder = `Add a new ${this.messageNoun ?? 'note'} here...`;
  }

  private getDefaultNewTags(): string[] {
    return [...this.getMessageTags(), ...this.resolvedDefaultTags];
  }

  /** The "Filter by tag" dropdown's options — restricted to messages carrying every `hiddenTags`
   * entry plus (if set) at least one `tagFilter` entry, so it never lists an unrelated tag. */
  private recomputeAvailableTags(): void {
    const hidden = this.resolvedHiddenTags;
    const visibleScope = this.visibleTags(this.resolvedTagFilter ?? []);

    const inScopeMessages = this.chatMessages.filter((m) => {
      if (!hidden.every((t) => m.tags.includes(t))) return false;
      return visibleScope.length === 0 || m.tags.some((t) => visibleScope.includes(t));
    });

    this.tags = Array.from(new Set(this.visibleTags(inScopeMessages.flatMap((m) => m.tags))));
  }

  /** Applies a freshly (re)loaded message list, regardless of source. */
  private applyMessages(messages: ChatMessage[]): void {
    this.chatMessages = [...messages].reverse();
    const seededTags = (this.selectedTags ?? []).length ? this.selectedTags : this.visibleTags(this.resolvedTagFilter ?? []);
    this.recomputeAvailableTags();
    this.isLoading = false;
    this.filterByTag(seededTags);
    this.cd.detectChanges();
  }

  /** The `groupTags` entry `message` carries, if any. */
  private groupTagFor(message: ChatMessage): string | undefined {
    return this.resolvedGroupTags.find((tag) => message.tags.includes(tag));
  }

  /** Groups messages into one contiguous run per `groupTags` entry (in `groupTags` order), each
   * keeping its relative order; ungrouped messages are appended at the end. No-op if empty. */
  private groupByGroupTags(messages: ChatMessage[]): ChatMessage[] {
    if (!this.resolvedGroupTags.length) return messages;

    const byGroup = new Map<string, ChatMessage[]>();
    const ungrouped: ChatMessage[] = [];
    for (const message of messages) {
      const tag = this.groupTagFor(message);
      if (!tag) {
        ungrouped.push(message);
        continue;
      }
      const bucket = byGroup.get(tag);
      if (bucket) {
        bucket.push(message);
      } else {
        byGroup.set(tag, [message]);
      }
    }

    return [...this.resolvedGroupTags.flatMap((tag) => byGroup.get(tag) ?? []), ...ungrouped];
  }

  private scrollToHighlightedNote(): boolean {
    if (!this.resolvedScrollToNoteId || this.hasScrolledToHighlight) return false;

    this.hasScrolledToHighlight = true;
    const highlighted = this.host.nativeElement.querySelector('.note-highlight');
    if (highlighted) {
      highlighted.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    }
    return false;
  }

  private getMessageTags(): string[] {
    const url = this.router.url;

    if (url.includes('performance-manager')) {
      return ['Performance'];
    } else if (url.includes('/safety')) {
      return ['Safety'];
    } else if (url.includes('/energy/health')) {
      return ['Health'];
    } else if (url.includes('/warranty-tracker')) {
      return ['Warranty'];
    } else if (url.includes('/data-explorer')) {
      return ['Data Explorer'];
    } else if (url.includes('/alerts')) {
      return ['Smart Alerts'];
    } else if (url.includes('/reports')) {
      return ['Reports'];
    } else if (url.includes('/storage-configuration')) {
      return ['Digital Onboarding'];
    } else {
      return [];
    }
  }
}
